import React from 'react';
import fr from 'facet-rest';
import {
  FDClickableText,
  FDEmptyState,
  FDSecondaryActionButton,
  FDStack,
  FDTable,
  FDTableBody,
  FDTableCell,
  FDTableHead,
  FDTableRow,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import TitleSection from 'Views/Page/Content/Modal/Components/TitleSection';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { MetaCollectionRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaCollectionRenderer';
import {
  SolutionRowOverrides,
  SolutionTableRow,
} from 'Views/Page/Content/Modal/Layouts/RetirementAgeGoalSeekResultsStep/solutionRow';
import { dataAttrs } from 'Helpers/Testing';

export const RetirementAgeGoalSeekResultsStepLayout = () => {
  const { displayErrorMessage } = useSnackbarContext();

  const {
    setModalCollection,
    loadModalCollectionByLink,
    clearModalCollection,
    modalResource: resource,
  } = useModalContext();

  const { refreshPageContentCollection } = usePageContentContext();

  const previousLink = resource.relationship(fr.relationships.previousLink.name)?.link('related');

  const hasSpouse = resource.attr('hasSpouse') === 'true';
  const displayCount = resource.attr('displayCount');

  const [showMoreButton, setShowMoreButton] = React.useState(true);

  const handleSubmit = async (solutionResource, fineTune) => {
    try {
      resource.attr('fineTune', fineTune);
      // Clear the solutions collection and only include the solution in question
      resource.attr('solutions').resources().removeAllItems();
      resource.attr('solutions').resources().pushItem(null, solutionResource);

      const resp = await resource.submit();

      if (fineTune !== 'true') {
        clearModalCollection();
        refreshPageContentCollection();
      } else {
        setModalCollection(resp);
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const hasRows = resource.attr('solutions').resources().length() > 0;

  return (
    <DialogContent>
      <FDStack direction={'column'} spacing={2} paddingX={4} paddingY={2}>
        <TitleSection>
          <MetaControlField attr={'title'} />
        </TitleSection>
        <FDStack direction={'column'} spacing={4} width={'100%'}>
          <FDStack direction={'column'} spacing={4} width={'100%'}>
            <MetaControlField attr={'disclaimer'} />
            {!hasRows && <FDEmptyState title={'No solutions found, please try again.'} icon={'close'} />}
            {hasRows && <>
              <FDStack direction={'column'} spacing={4}>
                <FDTable>
                  <FDTableHead>
                    <FDTableRow>
                      <FDTableCell>
                        <MetaControlField attr={'primaryClientRetirementHeader'} />
                      </FDTableCell>
                      {hasSpouse && <>
                        <FDTableCell>
                          <MetaControlField attr={'spouseClientRetirementHeader'} />
                        </FDTableCell>
                      </>}
                      <FDTableCell>
                        <MetaControlField attr={'endBalanceHeader'} />
                      </FDTableCell>
                      <FDTableCell data-empty-column />
                    </FDTableRow>
                  </FDTableHead>
                  <FDTableBody>
                    <MetaCollectionRenderer
                      collection={resource.attr('solutions')}
                      layout={SolutionTableRow}
                      LayoutProps={{
                        isPlural: hasSpouse,
                        submitSolution: handleSubmit,
                      }}
                      limit={(showMoreButton ? displayCount : undefined)}
                      metaOverrideSet={SolutionRowOverrides}
                    />
                  </FDTableBody>
                </FDTable>
                {showMoreButton && <FDSecondaryActionButton
                  label={`View more scenarios (${resource.attr('solutions').resources().length() - displayCount})`}
                  onClick={() => {
                    setShowMoreButton(false);
                  }}
                  {...dataAttrs('retirement-age-goal-seek-expand-results')}
                />}
              </FDStack>
            </>}
            <FDStack spacing={2} direction="row" alignItems={'center'} justifyContent={'center'} width={'100%'}>
              <FDClickableText
                label={'Start over'}
                onClick={() => {
                  loadModalCollectionByLink(previousLink.url());
                }}
                appearance={'minor'}
              />
            </FDStack>
          </FDStack>
        </FDStack>
      </FDStack>
    </DialogContent>
  );
};
